import { useEffect, useId, useRef, type MouseEvent } from 'react';

interface ScrollLockState {
  count: number;
  overflow: string;
}

const scrollLocks = new WeakMap<HTMLElement, ScrollLockState>();

const lockScroll = (element: HTMLElement) => {
  const existingLock = scrollLocks.get(element);
  if (existingLock) {
    existingLock.count += 1;
  } else {
    scrollLocks.set(element, { count: 1, overflow: element.style.overflow });
    element.style.overflow = 'hidden';
  }

  return () => {
    const lock = scrollLocks.get(element);
    if (!lock) {
      return;
    }
    lock.count -= 1;
    if (lock.count === 0) {
      element.style.overflow = lock.overflow;
      scrollLocks.delete(element);
    }
  };
};

export interface DeleteConfirmationModalProps {
  open: boolean;
  title: string;
  description: string;
  deleting?: boolean;
  confirmLabel?: string;
  deletingLabel?: string;
  onCancel: () => void;
  onConfirm: () => void;
}

const DeleteConfirmationModal = ({
  open,
  title,
  description,
  deleting = false,
  confirmLabel = 'Delete',
  deletingLabel = 'Deleting...',
  onCancel,
  onConfirm
}: DeleteConfirmationModalProps) => {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const deleteButtonRef = useRef<HTMLButtonElement>(null);
  const confirmPendingRef = useRef(false);

  useEffect(() => {
    if (!open || !deleting) {
      confirmPendingRef.current = false;
    }
  }, [deleting, open]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    deleteButtonRef.current?.focus();
    return () => previouslyFocused?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const moduleScroll = panelRef.current?.closest('.org-module__scroll');
    const scrollContainers = new Set<HTMLElement>([document.documentElement, document.body]);
    if (moduleScroll instanceof HTMLElement) {
      scrollContainers.add(moduleScroll);
    }
    const unlock = Array.from(scrollContainers, lockScroll);
    return () => unlock.forEach((release) => release());
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (!deleting) {
          event.preventDefault();
          onCancel();
        }
        return;
      }
      if (event.key !== 'Tab') {
        return;
      }
      const focusable = Array.from(
        panelRef.current?.querySelectorAll<HTMLElement>('button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])') ?? []
      );
      if (focusable.length === 0) {
        event.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && (document.activeElement === first || !panelRef.current?.contains(document.activeElement))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !panelRef.current?.contains(document.activeElement))) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [deleting, onCancel, open]);

  if (!open) {
    return null;
  }

  const handleOutsideClick = (event: MouseEvent<HTMLDivElement>) => {
    if (!deleting && event.currentTarget === event.target) {
      onCancel();
    }
  };

  const handleConfirm = () => {
    if (deleting || confirmPendingRef.current) {
      return;
    }
    confirmPendingRef.current = true;
    onConfirm();
  };

  return (
    <div
      className="integrations-delete-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      onClick={handleOutsideClick}
    >
      <div className="integrations-delete-modal__backdrop" onClick={handleOutsideClick} />
      <div ref={panelRef} className="integrations-delete-modal__panel">
        <header className="integrations-delete-modal__header">
          <h3 id={titleId}>{title}</h3>
        </header>
        <div className="integrations-delete-modal__body">
          <p>{description}</p>
        </div>
        <footer className="integrations-delete-modal__actions">
          <button type="button" className="ui-button ui-button--ghost" onClick={onCancel} disabled={deleting}>
            Cancel
          </button>
          <button ref={deleteButtonRef} type="button" className="ui-button ui-button--danger" disabled={deleting} onClick={handleConfirm}>
            {deleting ? deletingLabel : confirmLabel}
          </button>
        </footer>
      </div>
    </div>
  );
};

export default DeleteConfirmationModal;
